import React, { useState, useEffect, useMemo } from 'react';
import { collectionGroup, query, where, getDocs, getFirestore, doc, getDoc } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import DatePicker from 'react-datepicker';
import app from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext'; // Import useAuth

// Helper function to get a date in YYYY-MM-DD format based on the user's local timezone
const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// --- UPDATED: Daily Detail View Component ---
const DailyDetailView = ({ date, records, onBack }) => {
    const [animalData, setAnimalData] = useState([]);
    useEffect(() => {
        const processRecords = async () => {
            const db = getFirestore(app);
            const farmId = records[0]?.farmId;
            if (!farmId || records.length === 0) {
                setAnimalData([]);
                return;
            }

            // Create a map to aggregate data by animal ID for the selected date
            const aggregatedData = new Map();

            for (const r of records) {
                const animalId = r.path?.split('/')[7];
                if (!animalId) continue;

                const totalYield = (r.morningYield || 0) + (r.eveningYield || 0);
                const income = totalYield * (r.pricePerLiter || 0);

                if (aggregatedData.has(animalId)) {
                    const existing = aggregatedData.get(animalId);
                    existing.totalYield += totalYield;
                    existing.income += income;
                } else {
                    aggregatedData.set(animalId, { totalYield, income, animalName: 'Loading...' });
                }
            }
        const animalNamePromises = records.map(async (r) => {
            const animalId = r.path?.split('/')[7];
            if (!animalId) {
              return { id: null, name: 'Unknown' };
            }
            const pathWithoutId = r.path.replace(new RegExp(`/milk_records/${animalId}$`), '');

            const animalRef = doc(db, pathWithoutId);
            const animalSnap = await getDoc(animalRef);
          
            return {
              id: animalId,
              name: animalSnap.exists() ? animalSnap.data().name : 'Unknown',
            };
          });
          
        const animalNames = await Promise.all(animalNamePromises);

        animalNames.forEach(({ id, name }) => {
            if (aggregatedData.has(id)) {
                aggregatedData.get(id).animalName = name;
            }
        });
      
        
        const aggregatedByName = new Map();

        for (const { animalName, income, totalYield } of aggregatedData.values()) {
            const existing = aggregatedByName.get(animalName) || { animalName, income: 0, totalYield: 0 };
            existing.income += income || 0;
            existing.totalYield += totalYield || 0;
            aggregatedByName.set(animalName, existing);
        }
        
        // console.log(aggregatedByName);


        setAnimalData(Array.from(aggregatedByName.values()));
    }; 
        processRecords();
    }, [records]);

    return (
        <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Daily Report for {new Date(date + 'T00:00:00').toLocaleDateString()}</h3>
                <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800">&larr; Back to Overview</button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">Production by Animal</h4>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={animalData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="animalName" width={80} />
                            <Tooltip formatter={(value) => `${value.toFixed(1)} L`} />
                            <Legend />
                            <Bar dataKey="totalYield" name="Total Yield" fill="#8b5cf6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h4 className="text-lg font-semibold mb-4 text-gray-800">Income by Animal</h4>
                     <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={animalData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis type="category" dataKey="animalName" width={80} />
                            <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                            <Legend />
                            <Bar dataKey="income" name="Income" fill="#22c55e" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* --- NEW: Summary Table --- */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Production (L)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Income (₹)</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {animalData.map((animal, index) => (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{animal.animalName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{animal.totalYield.toFixed(1)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">{animal.income.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


const MilkDashboard = () => {
    const db = getFirestore(app);
    const { currentUser } = useAuth();
    const farmId = currentUser.farmId;

    const [allRecordsInRange, setAllRecordsInRange] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    const [endDate, setEndDate] = useState(new Date());
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 6);
        return date;
    });
    
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        const fetchMilkDataForRange = async () => {
            if (!farmId || !startDate || !endDate) {
                setAllRecordsInRange([]);
                return;
            }
            setIsLoading(true);

            const startDateStr = getLocalDateString(startDate);
            const endDateStr = getLocalDateString(endDate);

            const milkRecordsRef = collectionGroup(db, 'milk_records');
            const q = query(
                milkRecordsRef,
                where('farmId', '==', farmId),
                where('date', '>=', startDateStr),
                where('date', '<=', endDateStr)
            );

            const querySnapshot = await getDocs(q);
            const records = querySnapshot.docs.map(doc => ({...doc.data(), path: doc.ref.path}));
            setAllRecordsInRange(records);
            setIsLoading(false);
        };

        fetchMilkDataForRange();
    }, [farmId, startDate, endDate]);

    const { dailyData, periodTotals } = useMemo(() => {
        const groupedByDate = {};
        allRecordsInRange.forEach(record => {
            const date = record.date;
            if (!groupedByDate[date]) {
                groupedByDate[date] = [];
            }
            groupedByDate[date].push(record);
        });

        const dailySummaries = Object.keys(groupedByDate).map(date => {
            const records = groupedByDate[date];
            const totals = records.reduce((acc, curr) => {
                const totalYield = (curr.morningYield || 0) + (curr.eveningYield || 0);
                acc.morningYield += curr.morningYield || 0;
                acc.eveningYield += curr.eveningYield || 0;
                acc.totalYield += totalYield;
                acc.dailyIncome += totalYield * (curr.pricePerLiter || 0);
                return acc;
            }, { morningYield: 0, eveningYield: 0, totalYield: 0, dailyIncome: 0 });
            
            return {
                date,
                ...totals,
                formattedDate: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                records // Keep original records for drill-down
            };
        });

        const sortedData = dailySummaries.sort((a, b) => new Date(a.date) - new Date(b.date));

        const periodTotals = sortedData.reduce((acc, curr) => {
            acc.morning += curr.morningYield;
            acc.evening += curr.eveningYield;
            acc.total += curr.totalYield;
            acc.income += curr.dailyIncome;
            return acc;
        }, { morning: 0, evening: 0, total: 0, income: 0 });

        return { dailyData: sortedData, periodTotals };
    }, [allRecordsInRange]);

    const handleStartDateChange = (date) => {
        setStartDate(date);
        if (date > endDate) {
            setEndDate(date);
        }
    };
    
    const selectedDateRecords = useMemo(() => {
        if (!selectedDate) return [];
        const dayData = dailyData.find(d => d.date === selectedDate);
        return dayData ? dayData.records : [];
    }, [selectedDate, dailyData]);

    if (selectedDate) {
        return <DailyDetailView date={selectedDate} records={selectedDateRecords} onBack={() => setSelectedDate(null)} />;
    }

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Milk Production Tracker</h2>
                <div className="flex items-center space-x-2 mt-2 sm:mt-0">
                    <div>
                        <label htmlFor="startDate" className="text-sm font-medium text-gray-700 mr-2">From:</label>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            className="form-input rounded-md shadow-sm w-36"
                            portalId="portal-root"
                            popperClassName="high-z-datepicker"
                        />
                    </div>
                    <div>
                        <label htmlFor="endDate" className="text-sm font-medium text-gray-700 mr-2">To:</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            className="form-input rounded-md shadow-sm w-36"
                            portalId="portal-root"
                            popperClassName="high-z-datepicker"
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-4 rounded-lg shadow"><p className="text-sm text-gray-500">Total Morning Yield</p><p className="text-2xl font-bold text-blue-600">{periodTotals.morning.toFixed(1)} L</p></div>
                <div className="bg-white p-4 rounded-lg shadow"><p className="text-sm text-gray-500">Total Evening Yield</p><p className="text-2xl font-bold text-orange-600">{periodTotals.evening.toFixed(1)} L</p></div>
                <div className="bg-white p-4 rounded-lg shadow"><p className="text-sm text-gray-500">Grand Total Yield</p><p className="text-2xl font-bold text-purple-600">{periodTotals.total.toFixed(1)} L</p></div>
                <div className="bg-white p-4 rounded-lg shadow"><p className="text-sm text-gray-500">Total Income</p><p className="text-2xl font-bold text-green-600">₹{periodTotals.income.toFixed(2)}</p></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Production History</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="formattedDate" />
                            <YAxis label={{ value: 'Liters (L)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip formatter={(value) => `${value.toFixed(1)} L`} />
                            <Legend />
                            <Line type="monotone" dataKey="morningYield" name="Morning" stroke="#3b82f6" strokeWidth={2} />
                            <Line type="monotone" dataKey="eveningYield" name="Evening" stroke="#f97316" strokeWidth={2} />
                            <Line type="monotone" dataKey="totalYield" name="Total" stroke="#8b5cf6" strokeWidth={3} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Production vs. Income</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="formattedDate" />
                            <YAxis yAxisId="left" label={{ value: 'Liters (L)', angle: -90, position: 'insideLeft' }} />
                            <YAxis yAxisId="right" orientation="right" label={{ value: 'Income (₹)', angle: -90, position: 'insideRight' }} />
                            <Tooltip formatter={(value, name) => [name === 'Total Yield' ? `${value.toFixed(1)} L` : `₹${value.toFixed(2)}`, name]} />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="totalYield" name="Total Yield" stroke="#8b5cf6" strokeWidth={2} />
                            <Line yAxisId="right" type="monotone" dataKey="dailyIncome" name="Income" stroke="#22c55e" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
                 <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Morning Yield (L)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evening Yield (L)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Yield (L)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Daily Income (₹)</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                            <tr><td colSpan="5" className="text-center py-4">Loading milk data...</td></tr>
                        ) : (
                            [...dailyData].reverse().map(day => (
                                <tr key={day.date} onClick={() => setSelectedDate(day.date)} className="cursor-pointer hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">{new Date(day.date + 'T00:00:00').toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.morningYield.toFixed(1)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.eveningYield.toFixed(1)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">{day.totalYield.toFixed(1)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">{day.dailyIncome.toFixed(2)}</td>
                                </tr>
                            ))
                        )}
                        {!isLoading && dailyData.length === 0 && (
                            <tr><td colSpan="5" className="text-center py-4 text-gray-500">No milk records found for this date range.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MilkDashboard;
